// OperatorDepositRequests.jsx
import React, { useState, useEffect, useMemo } from "react";
import DepositRequestsTable from "./DepositRequestsTable";
import RejectionModal from "./RejectionModal";
import StatusBadge from "./StatusBadge";
import DepositService from "../../../service/DepositService";

function OperatorDepositRequests() {
  const [depositRequests, setDepositRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bannerMessage, setBannerMessage] = useState(""); // success/error banner
  const [bannerType, setBannerType] = useState("info");   // 'success' | 'error' | 'info'
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processing, setProcessing] = useState(false);

  const depositService = useMemo(() => new DepositService(), []);

  const showBanner = (msg, type = "info") => {
    setBannerMessage(msg);
    setBannerType(type);
    // Auto clear after a while (optional)
    setTimeout(() => setBannerMessage(""), 5000);
  };

  // Fetch all deposit requests
  const fetchDepositRequests = async () => {
    try {
      setLoading(true);
      setBannerMessage("");
      const result = await depositService.fetchDeposits({ items_per_page: 100 });
      setDepositRequests(result.data || []);
    } catch (error) {
      console.error("Error fetching deposit requests:", error);
      showBanner(error.message || "Failed to fetch deposit requests", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepositRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApprove = async (requestId) => {
    try {
      setProcessing(true);
      const res = await depositService.approveDeposit(requestId, "Deposit approved by admin");
      if (res?.success) {
        await fetchDepositRequests();
        showBanner("Deposit approved successfully!", "success");
      } else {
        showBanner(res?.message || "Failed to approve deposit", "error");
      }
    } catch (error) {
      console.error("Error approving deposit:", error);
      showBanner(error.response?.data?.message || error.message || "Failed to approve deposit", "error");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (requestId) => {
    if (!rejectionReason.trim()) {
      showBanner("Please enter a rejection reason", "error");
      return;
    }

    try {
      setProcessing(true);
      const res = await depositService.rejectDeposit(
        requestId,
        rejectionReason,
        "Deposit rejected by admin"
      );

      if (res?.success) {
        await fetchDepositRequests();
        showBanner("Deposit rejected successfully!", "success");
        setRejectionReason("");
        setSelectedRequest(null);
      } else {
        showBanner(res?.message || "Failed to reject deposit", "error");
      }
    } catch (error) {
      console.error("Error rejecting deposit:", error);
      showBanner(error.response?.data?.message || error.message || "Failed to reject deposit", "error");
    } finally {
      setProcessing(false);
    }
  };

  const openRejectModal = (request) => {
    setSelectedRequest(request);
    setRejectionReason("");
  };

  const closeRejectModal = () => {
    setSelectedRequest(null);
    setRejectionReason("");
  };

  const pendingRequests = depositRequests.filter((r) => r.status === "pending");
  const processedRequests = depositRequests.filter((r) => r.status !== "pending");

  if (loading) {
    return (
      <div className="text-white p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p>Loading deposit requests...</p>
      </div>
    );
  }

  return (
    <div className="text-white md:px-5 md:py-4 p-2 bg-transparent max-w-screen-lg mx-auto">
      <h1 className="md:text-[26px] text-[20px] font-semibold md:mb-6 mb-4 text-center md:text-left">
        Deposit Requests
      </h1>

      {bannerMessage && (
        <div
          className={`px-4 py-3 rounded-lg mb-6 ${
            bannerType === "success"
              ? "bg-green-500/20 border border-green-500 text-green-400"
              : bannerType === "error"
              ? "bg-red-500/20 border border-red-500 text-red-400"
              : "bg-blue-500/20 border border-blue-500 text-blue-300"
          }`}
        >
          {bannerMessage}
        </div>
      )}

      {/* Pending */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-yellow-600">
          Pending Requests ({pendingRequests.length})
        </h2>
        {pendingRequests.length === 0 ? (
          <p className="text-slate-400 text-center py-4">No pending deposit requests.</p>
        ) : (
          <DepositRequestsTable
            requests={pendingRequests}
            showActions
            processing={processing}
            onApprove={handleApprove}
            onReject={openRejectModal}
          />
        )}
      </div>

      {/* Processed */}
      <div>
        <h2 className="text-xl font-semibold mb-4 text-slate-800">
          Processed Requests ({processedRequests.length})
        </h2>
        {processedRequests.length === 0 ? (
          <p className="text-slate-400 text-center py-4">No processed deposit requests.</p>
        ) : (
          <DepositRequestsTable requests={processedRequests} showActions={false} />
        )}
      </div>

      {/* Rejection Modal */}
      <RejectionModal
        isOpen={!!selectedRequest}
        request={selectedRequest}
        rejectionReason={rejectionReason}
        onRejectionReasonChange={setRejectionReason}
        onConfirmReject={() => handleReject(selectedRequest?._id)}
        onCancel={closeRejectModal}
        type="deposit"
        processing={processing}
      />
    </div>
  );
}

export default OperatorDepositRequests;
